import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, filter, map, switchMap, take, timeout } from 'rxjs/operators';
import { from, of } from 'rxjs';
import { BCTXComputationHelper, InputAppAPIHelperUtil, InputAppErrorDisplay } from '@monorepo/utils';
import produce from 'immer';
import { ServerDefectivesStoreFacade } from './server-defectives-db.facade';
import {
  AcquireLockAndThenInitServerDefectives,
  AcquireLockAndThenLoadServerDefectives,
  InitServerDefectivesAndThenReleaseLock,
  LoadServerDefectivesAndThenReleaseLock,
  ReleaseLockAndThenShowErrorAndThenDismissLoader,
  ServerDefectivesNoAction,
  ServerDefectivesStoreActionTypes,
  SetServerDefectivesDBAndReleaseLockAndThenDismissLoader,
  SetServerDefectivesDBAndReleaseLockAndThenInitMasterdataListAndClientDB,
  ReleaseLock,
  SetServerDefectivesDBAndReleaseLockAndThenEmitForServerImagesBackFill,
} from './server-defectives-db.action';
import { InputAppClientDBFacade } from '../input-app-client-db/input-app-client-db.facade';
import { PersistentStorageService } from '../../persistent-storage/persistent-storage.service';
import { getServerDefectivesInitialState } from './server-defectives-db.reducer';
import { CustomLoggerService, InputAppDataService } from '@monorepo/services';
import { InitStoreFacade } from '../init-db/init-db.facade';
import { MasterdataListDBFacade } from '../master-data-list-db/master-data-list-db.facade';
import { InputAppMetadataDBFacade } from '../metadata/metadata.facade';
import { FETCHING_DEFECT_LIST_LOCK_RETRY_TIMEOUT_MS } from '../metadata/metadata.reducer';
import { RECTIFIED_LISING_SYNCING_KEY } from '@monorepo/types';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class ServerDefectivesStoreEffects {
  constructor(
    private action$: Actions,
    private inputAppDataService: InputAppDataService,
    private inputAppAPIHelperUtil: InputAppAPIHelperUtil,
    private serverDefectivesStoreFacade: ServerDefectivesStoreFacade,
    private inputAppClientDBFacade: InputAppClientDBFacade,
    private persistentStorageService: PersistentStorageService,
    private initStoreFacade: InitStoreFacade,
    private masterdataListFacade: MasterdataListDBFacade,
    private metadataFacade: InputAppMetadataDBFacade,
    private translate: TranslateService,
    private errorUtil: InputAppErrorDisplay,
    private customLoggerService: CustomLoggerService,
  ) {}

  AcquireLockAndThenInitServerDefectives$ = createEffect(() =>
    this.action$.pipe(
      ofType<AcquireLockAndThenInitServerDefectives>(
        ServerDefectivesStoreActionTypes.AcquireLockAndThenInitServerDefectives,
      ),
      switchMap((action) =>
        this.serverDefectivesStoreFacade.acquireLock(action.lockFor).pipe(
          take(1),
          switchMap((result) => {
            if (result.status === 'SUCCESS') {
              return of(new InitServerDefectivesAndThenReleaseLock(result.lockedBy));
            } else if (result.lockedBy === 'FETCHING_DEFECT_LIST') {
              return this.serverDefectivesStoreFacade.getLockDetailsAndNextCacheResetTime().pipe(
                filter(({ lockDetails }) => !lockDetails.isLocked),
                take(1),
                timeout(FETCHING_DEFECT_LIST_LOCK_RETRY_TIMEOUT_MS),
                switchMap(() => this.serverDefectivesStoreFacade.acquireLock(action.lockFor).pipe(take(1))),
                map((retryResult) => {
                  if (retryResult.status === 'SUCCESS') {
                    return new InitServerDefectivesAndThenReleaseLock(retryResult.lockedBy);
                  } else {
                    this.customLoggerService.log(
                      'SERVER_DEFECTIVES_DB_EFFECT',
                      'AcquireLockAndThenInitServerDefectives - retry acquireLock failed',
                      { retryResult },
                    );
                    this.inputAppAPIHelperUtil.showPopup(
                      this.translate.instant('input_app_common.error'),
                      retryResult.message,
                    );
                    return new ServerDefectivesNoAction();
                  }
                }),
                catchError((error) => {
                  this.customLoggerService.log(
                    'SERVER_DEFECTIVES_DB_EFFECT',
                    'AcquireLockAndThenInitServerDefectives - timed out waiting for lock release',
                    { error: JSON.stringify(error) },
                  );
                  this.inputAppAPIHelperUtil.showPopup(
                    this.translate.instant('input_app_common.error'),
                    this.translate.instant('server_defectives_db_effects.fetchingDefectiveListError.message'),
                  );
                  return of(new ServerDefectivesNoAction());
                }),
              );
            } else {
              this.inputAppAPIHelperUtil.showPopup(this.translate.instant('input_app_common.error'), result.message);
              return of(new ServerDefectivesNoAction());
            }
          }),
        ),
      ),
    ),
  );

  InitServerDefectivesAndThenReleaseLock$ = createEffect(() =>
    this.action$.pipe(
      ofType<InitServerDefectivesAndThenReleaseLock>(
        ServerDefectivesStoreActionTypes.InitServerDefectivesAndThenReleaseLock,
      ),
      switchMap((action) => {
        return this.initStoreFacade.getNextCacheResetTimestamp().pipe(
          take(1),
          map((nextCacheResetTimestamp) => [action, nextCacheResetTimestamp] as const),
        );
      }),
      switchMap(([action, nextCacheResetTimestamp]) =>
        from(this.persistentStorageService.get('[APP][INPUT_APP_SERVER_DEFECTIVES_DB]')).pipe(
          take(1),
          switchMap((db) => {
            const serverDefectivesInitialState = getServerDefectivesInitialState(nextCacheResetTimestamp);
            if (db && db.dailyCache.nextCacheResetTimestamp === nextCacheResetTimestamp) {
              return of(
                new SetServerDefectivesDBAndReleaseLockAndThenInitMasterdataListAndClientDB(action.lockedBy, db),
              );
            } else {
              return from(
                this.persistentStorageService.set(
                  '[APP][INPUT_APP_SERVER_DEFECTIVES_DB]',
                  serverDefectivesInitialState.serverDefectivesDB,
                ),
              ).pipe(
                take(1),
                map(() => {
                  return new SetServerDefectivesDBAndReleaseLockAndThenInitMasterdataListAndClientDB(
                    action.lockedBy,
                    serverDefectivesInitialState.serverDefectivesDB,
                  );
                }),
              );
            }
          }),
        ),
      ),
    ),
  );

  SetServerDefectivesDBAndReleaseLockAndThenInitMasterdataListAndClientDB$ = createEffect(() =>
    this.action$.pipe(
      ofType<SetServerDefectivesDBAndReleaseLockAndThenInitMasterdataListAndClientDB>(
        ServerDefectivesStoreActionTypes.SetServerDefectivesDBAndReleaseLockAndThenInitMasterdataListAndClientDB,
      ),
      map((action) => {
        this.masterdataListFacade.initializeMasterdataListStore(true);
        return new ServerDefectivesNoAction();
      }),
    ),
  );

  AcquireLockAndThenLoadServerDefectives$ = createEffect(() =>
    this.action$.pipe(
      ofType<AcquireLockAndThenLoadServerDefectives>(
        ServerDefectivesStoreActionTypes.AcquireLockAndThenLoadServerDefectives,
      ),
      switchMap((action) =>
        this.serverDefectivesStoreFacade.acquireLock(action.lockFor).pipe(
          take(1),
          switchMap((result) => {
            if (result.status === 'SUCCESS') {
              return of(new LoadServerDefectivesAndThenReleaseLock(result.lockedBy, action.loadWithoutLoader));
            } else if (result.lockedBy === 'FETCHING_DEFECT_LIST') {
              // Another re-fetch is already in flight and may have captured a stale payload
              // (e.g. selected station/shift changed after it started), so wait for it to
              // release the lock and retry with a fresh payload instead of silently no-op-ing.
              return this.serverDefectivesStoreFacade.getLockDetailsAndNextCacheResetTime().pipe(
                filter(({ lockDetails }) => !lockDetails.isLocked),
                take(1),
                timeout(FETCHING_DEFECT_LIST_LOCK_RETRY_TIMEOUT_MS),
                switchMap(() => this.serverDefectivesStoreFacade.acquireLock(action.lockFor).pipe(take(1))),
                map((retryResult) => {
                  if (retryResult.status === 'SUCCESS') {
                    return new LoadServerDefectivesAndThenReleaseLock(retryResult.lockedBy, action.loadWithoutLoader);
                  } else {
                    this.customLoggerService.log(
                      'SERVER_DEFECTIVES_DB_EFFECT',
                      'AcquireLockAndThenLoadServerDefectives - retry acquireLock failed',
                      { retryResult },
                    );
                    if (!action.loadWithoutLoader)
                      this.inputAppAPIHelperUtil.showPopup(
                        this.translate.instant('input_app_common.error'),
                        retryResult.message,
                      );
                    return new ServerDefectivesNoAction();
                  }
                }),
                catchError((error) => {
                  this.customLoggerService.log(
                    'SERVER_DEFECTIVES_DB_EFFECT',
                    'AcquireLockAndThenLoadServerDefectives - timed out waiting for lock release',
                    { error: JSON.stringify(error) },
                  );
                  if (!action.loadWithoutLoader)
                    this.inputAppAPIHelperUtil.showPopup(
                      this.translate.instant('input_app_common.error'),
                      this.translate.instant('server_defectives_db_effects.fetchingDefectiveListError.message'),
                    );
                  return of(new ServerDefectivesNoAction());
                }),
              );
            } else {
              this.customLoggerService.log(
                'SERVER_DEFECTIVES_DB_EFFECT',
                'AcquireLockAndThenLoadServerDefectives - acquireLock failed',
                { result },
              );
              if (!action.loadWithoutLoader)
                this.inputAppAPIHelperUtil.showPopup(this.translate.instant('input_app_common.error'), result.message);
              return of(new ServerDefectivesNoAction());
            }
          }),
        ),
      ),
    ),
  );

  LoadServerDefectivesAndThenReleaseLock$ = createEffect(() =>
    this.action$.pipe(
      ofType<LoadServerDefectivesAndThenReleaseLock>(
        ServerDefectivesStoreActionTypes.LoadServerDefectivesAndThenReleaseLock,
      ),
      switchMap((action) => {
        if (action.loadWithoutLoader) {
          return of(action);
        } else {
          this.metadataFacade.updateSyncingStatus(RECTIFIED_LISING_SYNCING_KEY, 'SYNCING');
          return from(
            this.errorUtil.presentLoadingWithId(
              'LOADING_SERVER_DEFECTIVES',
              this.translate.instant('server_defectives_db_effects.fetchingServerDefectives'),
            ),
          ).pipe(
            take(1),
            map(() => action),
          );
        }
      }),
      switchMap((action) =>
        this.serverDefectivesStoreFacade.getServerDefectivesPayload().pipe(
          take(1),
          map((res) => [res, action] as const),
        ),
      ),
      switchMap(([res, action]) =>
        this.inputAppClientDBFacade.getBctxItemsOrderWithoutSize().pipe(
          take(1),
          map((order) => [res, action, order] as const),
        ),
      ),
      switchMap(([requestPayload, action, bctxOrder]) =>
        this.inputAppClientDBFacade.getSelectedStationIdAndShiftId().pipe(
          take(1),
          map((selectedSubjectAndShiftId) => {
            return [requestPayload, selectedSubjectAndShiftId.shiftId, action, bctxOrder] as const;
          }),
        ),
      ),
      switchMap(([requestPayload, shiftId, action, bctxOrder]) => {
        return from(this.persistentStorageService.get('[APP][INPUT_APP_SERVER_DEFECTIVES_DB]')).pipe(
          take(1),
          map((currentDB) => {
            return [requestPayload, shiftId, currentDB, action, bctxOrder] as const;
          }),
        );
      }),
      switchMap(([requestPayload, shiftId, currentDB, action, bctxOrder]) =>
        this.inputAppDataService.getServerDefectives(requestPayload).pipe(
          switchMap((response) => {
            const transformedResponse = BCTXComputationHelper.transformToServerDefectives(response, shiftId, bctxOrder);
            const updatedDB = produce(currentDB, (draft) => {
              draft.dailyCache.lineWiseServerDefectives = transformedResponse;
              draft.dailyCache.lastSyncTime = new Date().getTime();
              draft.dailyCache.hasInitialized = true;
            });
            return from(this.persistentStorageService.set('[APP][INPUT_APP_SERVER_DEFECTIVES_DB]', updatedDB)).pipe(
              take(1),
              map(() => {
                this.metadataFacade.updateSyncingStatus(RECTIFIED_LISING_SYNCING_KEY, 'SYNCED');
                if (action.loadWithoutLoader) {
                  return new SetServerDefectivesDBAndReleaseLockAndThenEmitForServerImagesBackFill(
                    action.lockedBy,
                    updatedDB,
                  );
                } else {
                  return new SetServerDefectivesDBAndReleaseLockAndThenDismissLoader(action.lockedBy, updatedDB);
                }
              }),
            );
          }),
          catchError(() => {
            this.metadataFacade.updateSyncingStatus(RECTIFIED_LISING_SYNCING_KEY, 'FAILED');
            if (action.loadWithoutLoader) return of(new ReleaseLock(action.lockedBy));
            return of(
              new ReleaseLockAndThenShowErrorAndThenDismissLoader(
                action.lockedBy,
                this.translate.instant('server_defectives_db_effects.fetchingDefectiveListError.header'),
                this.translate.instant('server_defectives_db_effects.fetchingDefectiveListError.message'),
              ),
            );
          }),
        ),
      ),
    ),
  );

  SetServerDefectivesDBAndReleaseLockAndThenEmitForServerImagesBackFill$ = createEffect(() =>
    this.action$.pipe(
      ofType<SetServerDefectivesDBAndReleaseLockAndThenEmitForServerImagesBackFill>(
        ServerDefectivesStoreActionTypes.SetServerDefectivesDBAndReleaseLockAndThenEmitForServerImagesBackFill,
      ),
      map(() => {
        this.serverDefectivesStoreFacade.emitBackfillMissingServerImagesBuckets();
        return new ServerDefectivesNoAction();
      }),
    ),
  );

  SetServerDefectivesDBAndReleaseLockAndThenDismissLoader$ = createEffect(() =>
    this.action$.pipe(
      ofType<SetServerDefectivesDBAndReleaseLockAndThenDismissLoader>(
        ServerDefectivesStoreActionTypes.SetServerDefectivesDBAndReleaseLockAndThenDismissLoader,
      ),
      map(() => {
        this.serverDefectivesStoreFacade.emitBackfillMissingServerImagesBuckets();
        this.errorUtil.dismissLoadingWithId('LOADING_SERVER_DEFECTIVES');
        return new ServerDefectivesNoAction();
      }),
    ),
  );

  ReleaseLockAndThenShowErrorAndThenDismissLoader$ = createEffect(() =>
    this.action$.pipe(
      ofType<ReleaseLockAndThenShowErrorAndThenDismissLoader>(
        ServerDefectivesStoreActionTypes.ReleaseLockAndThenShowErrorAndThenDismissLoader,
      ),
      switchMap((action) => {
        return from(this.inputAppAPIHelperUtil.showPopup(action.title, action.message)).pipe(
          take(1),
          map(() => {
            this.errorUtil.dismissLoadingWithId('LOADING_SERVER_DEFECTIVES');
            return new ServerDefectivesNoAction();
          }),
        );
      }),
    ),
  );
}
